import React from 'react';
import cn from 'classnames';
import { Tooltip } from '_atoms';
import { Status } from '_atoms/Status';
import { Typography } from '_atoms/Typography';
import { dateFormatter } from 'helpers/dateFormatter';
import { PROMOTION_REQUEST_STATUS_TYPES } from 'constants/promotionRequest';
import { ReactComponent as Sort } from 'dist/icons/arrow_ascending_descending.svg';
import type { TableColumnType, TableRow } from '_atoms/Table/types';
import type { PromotionRequestLead } from 'actions';
import type { TableSorting } from './utils';

type SetSorting = (sort: TableSorting) => void;

export const INITIAL_TABLE_VALUES = {
  sorting: { field: 'created_at', key: 'created_at', order: 'desc' },
  limit: { value: 30, label: 30 },
  page: 1,
  offset: 0,
  itemsCount: 0,
};

const COLUMNS_WIDTH = {
  request_title: 240,
  recruiter_leads: 200,
  promotion_source: 180,
  created_at: 150,
  status: 120,
  openings_count: 100,
};

const headerCellRender =
  (title: string, field: string, key: string, sort: TableSorting, setSorting: SetSorting) => () => {
    const order = sort.key === key ? sort.order : null;
    const onClick = () => setSorting({ field, key, order: order === 'asc' ? 'desc' : 'asc' });
    return (
      <>
        {title}
        <Sort className={cn(order)} onClick={onClick} />
      </>
    );
  };

export const COLUMNS = (sort: TableSorting, setSorting: SetSorting): TableColumnType[] => [
  {
    title: headerCellRender('Request title', 'request_title', 'request_title', sort, setSorting),
    sorter: true,
    dataIndex: 'request_title',
    rowKey: 'request_title',
    width: COLUMNS_WIDTH.request_title,
    ellipsis: true,
    className: 'promotion-requests-table__cell',
    render: (value, record: TableRow) => {
      const jobUuid = (record.job as { uuid: string }).uuid;
      return (
        <a className="promotion-requests-table__title-link" href={`/jobs/${jobUuid}/promotion-request/${record.uuid}`}>
          <Typography.Text>{value as string}</Typography.Text>
        </a>
      );
    },
  },
  {
    title: 'Recruiter Lead',
    dataIndex: 'recruiter_leads',
    rowKey: 'recruiter_leads',
    width: COLUMNS_WIDTH.recruiter_leads,
    ellipsis: true,
    className: 'promotion-requests-table__cell',
    render: value => {
      const names = ((value as PromotionRequestLead[]) ?? []).map(lead => lead.full_name).join(', ');
      return <Tooltip label={names}>{names}</Tooltip>;
    },
  },
  {
    title: headerCellRender('Promotion source', 'promotion_source', 'promotion_source', sort, setSorting),
    sorter: true,
    dataIndex: 'promotion_source',
    rowKey: 'promotion_source',
    width: COLUMNS_WIDTH.promotion_source,
    ellipsis: true,
    className: 'promotion-requests-table__cell',
    render: value => value as string,
  },
  {
    title: headerCellRender('Creation date', 'created_at', 'created_at', sort, setSorting),
    sorter: true,
    dataIndex: 'created_at',
    rowKey: 'created_at',
    width: COLUMNS_WIDTH.created_at,
    className: 'promotion-requests-table__cell',
    render: value => (value ? dateFormatter(value as string) : ''),
  },
  {
    title: headerCellRender('Status', 'status', 'status', sort, setSorting),
    sorter: true,
    dataIndex: 'status',
    rowKey: 'status',
    width: COLUMNS_WIDTH.status,
    className: 'promotion-requests-table__cell',
    render: value => <Status type={PROMOTION_REQUEST_STATUS_TYPES[value as string]}>{value as string}</Status>,
  },
  {
    title: headerCellRender('Openings', 'openings_count', 'openings_count', sort, setSorting),
    sorter: true,
    dataIndex: 'openings_count',
    rowKey: 'openings_count',
    width: COLUMNS_WIDTH.openings_count,
    align: 'right',
    className: 'promotion-requests-table__cell promotion-requests-table__cell_count',
    render: (value, record: TableRow) => {
      const labels = (record.openingsLabel as string[]) ?? [];
      const count = <Typography.Text>{value as number}</Typography.Text>;
      return labels.length ? (
        <Tooltip
          label={labels.map((label, idx) => (
            <div key={`${label}.${idx}`}>{label}</div>
          ))}
        >
          {count}
        </Tooltip>
      ) : (
        count
      );
    },
  },
];
